import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, loading, isWhitelisted, user, checkWhitelistStatus } = useAuth();
  const [whitelistCheckTimeout, setWhitelistCheckTimeout] = useState(false);

  useEffect(() => {
    // Only check if auth is done loading, user is authenticated, and we don't have whitelist status yet
    // Don't trigger if AuthContext is already checking (it will handle it)
    if (!loading && isAuthenticated && user && isWhitelisted === null) {
      console.log('ProtectedRoute: Triggering whitelist check for user:', user.id);
      // Just trigger the check - AuthContext will handle caching
      checkWhitelistStatus(user.id).catch((err) => {
        console.error('ProtectedRoute: Error checking whitelist:', err);
      });
      
      // Set a timeout as a fallback
      const timeout = setTimeout(() => {
        if (isWhitelisted === null) {
          console.warn('Whitelist check taking longer than expected');
          setWhitelistCheckTimeout(true);
        }
      }, 6000); // Increased timeout since AuthContext handles the actual query timeout
      
      return () => clearTimeout(timeout);
    } else {
      setWhitelistCheckTimeout(false);
    }
  }, [loading, isAuthenticated, user, isWhitelisted, checkWhitelistStatus]);

  // Show loading only while auth is loading OR while checking whitelist for authenticated user
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Loading...</div>
      </div>
    );
  }

  // If authenticated but whitelist status is still null, wait a bit more (checkWhitelistStatus is running)
  // Show a minimal loading state to prevent flashing
  if (isAuthenticated && user && isWhitelisted === null) {
    // Show a subtle loading state - don't flash pending approval
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div style={{ fontSize: '14px', color: '#666' }}>Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/signin" replace />;
  }

  // If user is authenticated but not whitelisted, redirect to pending page
  if (isWhitelisted === false) {
    return <Navigate to="/pending-approval" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;

